import { useForm, ValidationError } from "@formspree/react";

const services = [
  {
    id: "voter-targeting",
    title: "Voter Targeting",
    summary: "Know exactly who to talk to and what to say.",
    details: [
      "District and precinct analysis",
      "Voter file modeling and universe building",
      "Persuasion and turnout targeting",
      "Opposition and crossover voter identification",
    ],
  },
  {
    id: "field-management",
    title: "Field Management",
    summary: "End-to-end management of your ground game.",
    details: [
      "Volunteer recruitment and training",
      "Canvass program development and oversight",
      "Walkbook design and turf assignment",
      "Phone and text bank coordination",
    ],
  },
  {
    id: "campaign-strategy",
    title: "Campaign Strategy",
    summary: "A winning plan from kickoff to election night.",
    details: [
      "Full-cycle campaign planning and roadmapping",
      "Messaging framework and narrative development",
      "Rapid response and opposition coordination",
      "Earned media and coalition strategy",
    ],
  },
];

const approach = [
  {
    step: "01",
    title: "Assess",
    body: "We start with a deep look at the district, the electorate, and the resources available. No two races are alike — we don't use cookie-cutter plans.",
  },
  {
    step: "02",
    title: "Build",
    body: "We develop a field plan built around your universe, your volunteers, and your timeline. Every contact is intentional.",
  },
  {
    step: "03",
    title: "Execute",
    body: "We work alongside your team in the field — not from a desk. Real-time adjustments based on what the doors are telling us.",
  },
  {
    step: "04",
    title: "Win",
    body: "Victory is the goal. We hold ourselves accountable to the numbers and don't stop grinding until the last ballot is in.",
  },
];

export function StrategyPage() {
  return (
    <div className="flex flex-col">

      {/* Hero */}
      <section className="bg-zinc-900 px-6 py-28">
        <div className="max-w-4xl mx-auto">
          <span className="text-xs font-semibold uppercase tracking-widest text-primary mb-5 block">
            Strategy
          </span>
          <h1 className="text-5xl md:text-6xl font-bold text-white tracking-tight leading-[1.1] mb-6">
            Field strategy<br />that wins races
          </h1>
          <p className="text-lg text-zinc-400 max-w-xl leading-relaxed mb-10">
            Voter targeting, field management, and full-cycle campaign strategy from
            practitioners who have run programs at every level of the ballot.
          </p>
          <div className="flex flex-col sm:flex-row gap-6">
            <a
              href="#contact"
              className="text-sm font-medium text-white underline underline-offset-4 hover:text-primary transition-colors"
            >
              Work with us
            </a>
            <a
              href="#services"
              className="text-sm font-medium text-zinc-400 hover:text-white transition-colors"
            >
              Our services →
            </a>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="bg-white border-b border-border/60">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <div className="mb-14">
            <span className="text-xs font-semibold uppercase tracking-widest text-primary">Services</span>
            <h2 className="text-3xl font-bold tracking-tight mt-2">What we do</h2>
            <p className="text-muted-foreground mt-3 max-w-xl">
              We offer the full stack of field services — whether you need targeted support in one
              area or a team to run your entire ground game.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((s) => (
              <div key={s.id} className="border border-border/60 rounded-xl p-7 flex flex-col gap-5">
                <div>
                  <div className="w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center mb-4">
                    <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                  </div>
                  <h3 className="font-semibold text-lg text-foreground">{s.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{s.summary}</p>
                </div>
                <ul className="space-y-2">
                  {s.details.map((d) => (
                    <li key={d} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <span className="text-primary mt-0.5 shrink-0">—</span>
                      {d}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="bg-zinc-50 border-b border-border/60">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <div className="mb-14">
            <span className="text-xs font-semibold uppercase tracking-widest text-primary">Approach</span>
            <h2 className="text-3xl font-bold tracking-tight mt-2">How we work</h2>
            <p className="text-muted-foreground mt-3 max-w-xl">
              We&apos;re not consultants who send slide decks. We&apos;re in the field alongside your team,
              doing the work.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-8">
            {approach.map((a) => (
              <div key={a.step} className="space-y-3">
                <span className="text-3xl font-bold text-primary/20">{a.step}</span>
                <h3 className="font-semibold text-foreground">{a.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{a.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact / Work With Us */}
      <section id="contact" className="bg-zinc-900">
        <div className="max-w-4xl mx-auto px-6 py-24">
          <div className="mb-12">
            <span className="text-xs font-semibold uppercase tracking-widest text-primary">Work With Us</span>
            <h2 className="text-3xl font-bold text-white tracking-tight mt-2">
              Tell us about your race
            </h2>
            <p className="text-zinc-400 mt-3 max-w-lg">
              Every engagement starts with a conversation. Fill out the form below and we&apos;ll be
              in touch within 48 hours.
            </p>
          </div>

          <ContactForm />
        </div>
      </section>

    </div>
  );
}

function ContactForm() {
  const [state, handleSubmit] = useForm("xpqerapp");

  if (state.succeeded) {
    return (
      <div className="rounded-lg border border-zinc-700 bg-zinc-800 p-8 text-center space-y-2">
        <p className="font-semibold text-white">Message received.</p>
        <p className="text-sm text-zinc-400">We&apos;ll be in touch within 48 hours.</p>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => void handleSubmit(e)}
      className="grid sm:grid-cols-2 gap-5"
    >
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-zinc-300">Name</label>
        <input
          type="text"
          name="name"
          required
          placeholder="Jane Smith"
          className="flex h-9 w-full rounded-md border border-zinc-700 bg-zinc-800 px-3 py-1 text-sm text-white placeholder:text-zinc-500 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary transition-colors"
        />
      </div>

      <div className="space-y-1.5">
        <label className="text-sm font-medium text-zinc-300">Email</label>
        <input
          type="email"
          name="email"
          required
          placeholder="jane@campaign.org"
          className="flex h-9 w-full rounded-md border border-zinc-700 bg-zinc-800 px-3 py-1 text-sm text-white placeholder:text-zinc-500 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary transition-colors"
        />
        <ValidationError field="email" errors={state.errors} className="text-xs text-red-400" />
      </div>

      <div className="space-y-1.5">
        <label className="text-sm font-medium text-zinc-300">Race / Office</label>
        <input
          type="text"
          name="race"
          required
          placeholder="State Senate District 14"
          className="flex h-9 w-full rounded-md border border-zinc-700 bg-zinc-800 px-3 py-1 text-sm text-white placeholder:text-zinc-500 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary transition-colors"
        />
      </div>

      <div className="space-y-1.5">
        <label className="text-sm font-medium text-zinc-300">Election date</label>
        <input
          type="text"
          name="election_date"
          placeholder="November 2026"
          className="flex h-9 w-full rounded-md border border-zinc-700 bg-zinc-800 px-3 py-1 text-sm text-white placeholder:text-zinc-500 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary transition-colors"
        />
      </div>

      <div className="sm:col-span-2 space-y-1.5">
        <label className="text-sm font-medium text-zinc-300">What do you need help with?</label>
        <textarea
          name="message"
          rows={4}
          placeholder="Tell us about your race, your team, and what kind of support you're looking for..."
          className="flex w-full rounded-md border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-white placeholder:text-zinc-500 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary transition-colors resize-none"
        />
      </div>

      <div className="sm:col-span-2 space-y-3">
        <ValidationError errors={state.errors} className="text-sm text-red-400" />
        <button
          type="submit"
          disabled={state.submitting}
          className="bg-primary hover:bg-primary/90 disabled:opacity-60 text-white text-sm font-semibold rounded-md px-6 py-2.5 transition-colors"
        >
          {state.submitting ? "Sending…" : "Send message"}
        </button>
      </div>
    </form>
  );
}
